// ============================================================================
// PRINT SERVICE
// ============================================================================
// Staff calls of the Print module (contract section 6): printers, Print Agents,
// pairing codes, the print queue and alerts. The module is neutral; DineOps
// only reads a ticket's print status and asks for a reprint (dineOpsService).
//
// Every path goes through buildEndpoint / buildEndpointWithQuery with a RAW
// path: both add the /v1 segment. A bare `/print/...` would reach
// `/api/print/...` and 404.

import { get, post, put, del } from '../client/axiosClient';
import { buildEndpoint, buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  PaginatedResponse,
  ID,
  Printer,
  PrinterLookup,
  PrinterQuery,
  CreatePrinterRequest,
  UpdatePrinterRequest,
  PrintAgent,
  PrintAgentQuery,
  UpdatePrintAgentRequest,
  CreatePairingCodeRequest,
  PairingCodeCreated,
  PairingCode,
  PrintJob,
  PrintJobQuery,
  PrintAlerts,
} from '../types';

const BASE = '/print';

/** Unwraps the standard envelope's PagedResult into the frontend PaginatedResponse. */
function toPaged<T>(raw: any): PaginatedResponse<T> {
  const paged = raw?.data ?? {};
  return {
    success: raw?.success,
    message: raw?.message,
    data: paged.items ?? [],
    pagination: {
      currentPage: paged.page ?? 1,
      pageSize: paged.pageSize ?? 20,
      totalPages: paged.totalPages ?? 0,
      totalItems: paged.totalCount ?? 0,
      hasNextPage: paged.hasNext ?? false,
      hasPreviousPage: paged.hasPrevious ?? false,
    },
  };
}

class PrintService {
  // ------------------------------------------------------------------ printers

  async getPrinters(params?: PrinterQuery): Promise<PaginatedResponse<Printer>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/printers`, params ?? {}));
    return toPaged<Printer>(res.data);
  }

  /** Active printers for dropdowns (department form, Print elsewhere). */
  async getPrinterLookup(): Promise<PrinterLookup[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/printers/lookup`));
    return res.data?.data ?? [];
  }

  async getPrinter(id: ID): Promise<Printer> {
    const res = await get<any>(buildEndpoint(`${BASE}/printers/${id}`));
    return res.data?.data;
  }

  async createPrinter(body: CreatePrinterRequest): Promise<Printer> {
    const res = await post<any>(buildEndpoint(`${BASE}/printers`), body);
    return res.data?.data;
  }

  async updatePrinter(id: ID, body: UpdatePrinterRequest): Promise<Printer> {
    const res = await put<any>(buildEndpoint(`${BASE}/printers/${id}`), body);
    return res.data?.data;
  }

  /** Soft delete. 409 with the API's message when a department still uses it. */
  async deletePrinter(id: ID, rowVersion: number): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/printers/${id}?rowVersion=${rowVersion}`));
  }

  /** Queues a test page; returns the job, to follow its status. */
  async testPrinter(id: ID): Promise<PrintJob> {
    const res = await post<any>(buildEndpoint(`${BASE}/printers/${id}/test`), {});
    return res.data?.data;
  }

  // -------------------------------------------------------------------- agents

  async getAgents(params?: PrintAgentQuery): Promise<PaginatedResponse<PrintAgent>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/agents`, params ?? {}));
    return toPaged<PrintAgent>(res.data);
  }

  async getAgent(id: ID): Promise<PrintAgent> {
    const res = await get<any>(buildEndpoint(`${BASE}/agents/${id}`));
    return res.data?.data;
  }

  async updateAgent(id: ID, body: UpdatePrintAgentRequest): Promise<PrintAgent> {
    const res = await put<any>(buildEndpoint(`${BASE}/agents/${id}`), body);
    return res.data?.data;
  }

  async approveAgent(id: ID): Promise<PrintAgent> {
    const res = await post<any>(buildEndpoint(`${BASE}/agents/${id}/approve`), {});
    return res.data?.data;
  }

  /** A reason is required. */
  async suspendAgent(id: ID, reason: string): Promise<PrintAgent> {
    const res = await post<any>(buildEndpoint(`${BASE}/agents/${id}/suspend`), { reason });
    return res.data?.data;
  }

  async resumeAgent(id: ID): Promise<PrintAgent> {
    const res = await post<any>(buildEndpoint(`${BASE}/agents/${id}/resume`), {});
    return res.data?.data;
  }

  /** A reason is required. Final: the agent's key is refused from now on. */
  async revokeAgent(id: ID, reason: string): Promise<PrintAgent> {
    const res = await post<any>(buildEndpoint(`${BASE}/agents/${id}/revoke`), { reason });
    return res.data?.data;
  }

  /** Unused, unexpired pairing codes. */
  async getPairingCodes(): Promise<PairingCode[]> {
    const res = await get<any>(buildEndpoint(`${BASE}/agents/pairing-codes`));
    return res.data?.data ?? [];
  }

  /** The code comes back once, here; the server keeps only its hash. */
  async createPairingCode(body: CreatePairingCodeRequest): Promise<PairingCodeCreated> {
    const res = await post<any>(buildEndpoint(`${BASE}/agents/pairing-codes`), body);
    return res.data?.data;
  }

  async withdrawPairingCode(id: ID): Promise<void> {
    await del<any>(buildEndpoint(`${BASE}/agents/pairing-codes/${id}`));
  }

  // ---------------------------------------------------------------------- jobs

  async getJobs(params?: PrintJobQuery): Promise<PaginatedResponse<PrintJob>> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/jobs`, params ?? {}));
    return toPaged<PrintJob>(res.data);
  }

  /** The job with its event history. */
  async getJob(id: ID): Promise<PrintJob> {
    const res = await get<any>(buildEndpoint(`${BASE}/jobs/${id}`));
    return res.data?.data;
  }

  async retryJob(id: ID): Promise<PrintJob> {
    const res = await post<any>(buildEndpoint(`${BASE}/jobs/${id}/retry`), {});
    return res.data?.data;
  }

  /** Print elsewhere: a new job on another printer, marked REROUTED. */
  async rerouteJob(id: ID, printerId: ID): Promise<PrintJob> {
    const res = await post<any>(buildEndpoint(`${BASE}/jobs/${id}/reroute`), { printerId });
    return res.data?.data;
  }

  async cancelJob(id: ID, reason: string): Promise<PrintJob> {
    const res = await post<any>(buildEndpoint(`${BASE}/jobs/${id}/cancel`), { reason });
    return res.data?.data;
  }

  /** Mark handled: takes a failed or rerouted job off the alerts. */
  async resolveJob(id: ID, note?: string): Promise<PrintJob> {
    const res = await post<any>(buildEndpoint(`${BASE}/jobs/${id}/resolve`), { note });
    return res.data?.data;
  }

  // -------------------------------------------------------------------- alerts

  /** Unresolved failed / rerouted jobs and offline agents, for one outlet when scopeId is given. */
  async getAlerts(scopeId?: ID | null): Promise<PrintAlerts> {
    const res = await get<any>(buildEndpointWithQuery(`${BASE}/alerts`, { scopeId: scopeId || undefined }));
    return res.data?.data ?? { failedJobs: [], offlineAgents: [], queuedCount: 0 };
  }
}

export const printService = new PrintService();
export default printService;
